import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Activity, 
  Zap, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Clock,
  Gauge,
  Settings
} from 'lucide-react';
import { usePerformanceOptimizer } from '@/hooks/usePerformanceOptimizer';
import { useHealthCheck } from '@/hooks/useHealthCheck';

interface PerformanceMetrics {
  memoryUsage: number;
  loadTime: number;
  errorRate: number;
  healthStatus: 'healthy' | 'degraded' | 'unhealthy';
}

export function PerformanceMonitor() {
  const [isVisible, setIsVisible] = useState(false);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    memoryUsage: 0,
    loadTime: 0,
    errorRate: 0,
    healthStatus: 'healthy'
  });
  
  // Usar nuestros hooks optimizados
  const { getStats, throttledLog, shouldLogError } = usePerformanceOptimizer({
    debugMode: true,
    maxErrorsPerMinute: 3,
    maxHealthChecksPerMinute: 1
  });
  
  const { 
    isHealthy, 
    responseTime, 
    lastCheck, 
    consecutiveFailures, 
    forceCheck,
    isChecking 
  } = useHealthCheck(90000); // 90 segundos entre checks
  
  // Calcular métricas en tiempo real
  useEffect(() => {
    const updateMetrics = () => {
      try {
        // Memoria (si está disponible)
        const memory = (performance as any).memory;
        const memoryUsage = memory ? (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100 : 0;
        
        // Performance timing
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const loadTime = navigation ? navigation.loadEventEnd - navigation.loadEventStart : 0;
        
        // Determinar estado de salud
        let healthStatus: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
        if (!isHealthy || consecutiveFailures > 1) {
          healthStatus = consecutiveFailures > 2 ? 'unhealthy' : 'degraded';
        }
        
        // Calcular tasa de error aproximada
        const stats = getStats();
        const errorRate = Math.min((stats.activeErrorKeys / 10) * 100, 100);
        
        setMetrics({
          memoryUsage: Math.round(memoryUsage),
          loadTime: Math.round(loadTime),
          errorRate: Math.round(errorRate),
          healthStatus
        });
      } catch (error) {
        if (shouldLogError('performance-metrics')) {
          throttledLog('error', 'Error updating performance metrics:', error);
        }
      }
    };
    
    // Actualizar inmediatamente y luego cada 30 segundos
    updateMetrics();
    const interval = setInterval(updateMetrics, 30000);
    
    return () => clearInterval(interval);
  }, [isHealthy, consecutiveFailures, getStats, shouldLogError, throttledLog]);
  
  const getHealthStatusIcon = () => {
    switch (metrics.healthStatus) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'unhealthy':
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };
  
  const getHealthStatusColor = () => {
    switch (metrics.healthStatus) {
      case 'healthy': return 'bg-green-500';
      case 'degraded': return 'bg-yellow-500';
      case 'unhealthy': return 'bg-red-500';
    }
  };
  
  const formatTime = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };
  
  const formatLastCheck = (date: Date | null) => {
    if (!date) return 'Nunca';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    if (minutes > 0) {
      return `Hace ${minutes}m ${seconds}s`;
    }
    return `Hace ${seconds}s`;
  };
  
  // Monitor flotante minimalista
  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsVisible(true)}
          className="bg-background/80 backdrop-blur-sm border-border/50 hover:bg-accent"
        >
          <Activity className="h-4 w-4 mr-2" />
          <div className={`w-2 h-2 rounded-full ${getHealthStatusColor()}`} />
        </Button>
      </div>
    );
  }
  
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80">
      <Card className="bg-background/95 backdrop-blur-sm border-border/50 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              Monitor de Rendimiento
            </CardTitle>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsVisible(false)}
              className="h-6 w-6 p-0"
            >
              ×
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Estado General */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getHealthStatusIcon()}
              <span className="text-sm font-medium">
                Sistema {metrics.healthStatus === 'healthy' ? 'Saludable' : 
                        metrics.healthStatus === 'degraded' ? 'Degradado' : 'Crítico'}
              </span>
            </div>
            <Badge variant={metrics.healthStatus === 'healthy' ? 'default' : 'destructive'}>
              {consecutiveFailures > 0 ? `${consecutiveFailures} fallos` : 'OK'}
            </Badge>
          </div>
          
          {/* Health Check */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Health Check</span>
              <div className="flex items-center gap-2">
                {isChecking && <Clock className="h-3 w-3 animate-spin" />}
                <span className="text-muted-foreground">
                  {formatTime(responseTime)}
                </span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {formatLastCheck(lastCheck)}
            </div>
          </div>
          
          {/* Memoria */}
          {metrics.memoryUsage > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Uso de Memoria</span>
                <span className="text-muted-foreground">{metrics.memoryUsage}%</span>
              </div>
              <Progress 
                value={metrics.memoryUsage} 
                className="h-2"
              />
            </div>
          )}
          
          {/* Tasa de Errores */}
          {metrics.errorRate > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Tasa de Errores</span>
                <span className="text-muted-foreground">{metrics.errorRate}%</span>
              </div>
              <Progress 
                value={metrics.errorRate} 
                className="h-2"
              />
            </div>
          )}
          
          {/* Estadísticas del Optimizador */}
          <div className="text-xs text-muted-foreground border-t pt-2">
            <div className="flex justify-between">
              <span>Errores activos:</span>
              <span>{getStats().activeErrorKeys}</span>
            </div>
            <div className="flex justify-between">
              <span>Health checks:</span>
              <span>{getStats().recentHealthChecks}</span>
            </div>
          </div>
          
          {/* Acciones */}
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={forceCheck}
              disabled={isChecking}
              className="flex-1"
            >
              <Zap className="h-3 w-3 mr-1" />
              Test
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                console.log('📊 Performance Stats:', getStats());
                console.log('🏥 Health Status:', { isHealthy, responseTime, consecutiveFailures });
                console.log('📈 Metrics:', metrics);
              }}
              className="flex-1"
            >
              <Settings className="h-3 w-3 mr-1" />
              Log
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Componente para desarrollo - solo se muestra en desarrollo
export function DevPerformanceMonitor() {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }
  
  return <PerformanceMonitor />;
}